import { useState, useEffect } from 'react';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import { 
  Cloud, Sun, CloudRain, Wind, Waves, Thermometer, Navigation, 
  MapPin, RefreshCw, AlertTriangle, Droplets, ChevronDown, ChevronUp,
  Sunrise, Sunset, Eye, Gauge, ArrowUp, Moon
} from 'lucide-react';
import { toast } from 'sonner';

// Città costiere del Mediterraneo (tutti i paesi)
const COASTAL_CITIES = [
  // Italia
  { name: 'Genova', country: '🇮🇹', lat: 44.4056, lon: 8.9463 },
  { name: 'Napoli', country: '🇮🇹', lat: 40.8518, lon: 14.2681 },
  { name: 'Venezia', country: '🇮🇹', lat: 45.4408, lon: 12.3155 },
  { name: 'Palermo', country: '🇮🇹', lat: 38.1157, lon: 13.3615 },
  // Spagna
  { name: 'Barcellona', country: '🇪🇸', lat: 41.3851, lon: 2.1734 },
  { name: 'Valencia', country: '🇪🇸', lat: 39.4699, lon: -0.3763 },
  { name: 'Malaga', country: '🇪🇸', lat: 36.7213, lon: -4.4214 },
  { name: 'Palma', country: '🇪🇸', lat: 39.5696, lon: 2.6502 },
  // Francia
  { name: 'Marsiglia', country: '🇫🇷', lat: 43.2965, lon: 5.3698 },
  { name: 'Nizza', country: '🇫🇷', lat: 43.7102, lon: 7.2620 },
  { name: 'Cannes', country: '🇫🇷', lat: 43.5528, lon: 7.0174 },
  // Grecia
  { name: 'Atene', country: '🇬🇷', lat: 37.9838, lon: 23.7275 },
  { name: 'Salonicco', country: '🇬🇷', lat: 40.6401, lon: 22.9444 },
  { name: 'Creta', country: '🇬🇷', lat: 35.2401, lon: 24.8093 },
  // Croazia
  { name: 'Spalato', country: '🇭🇷', lat: 43.5081, lon: 16.4402 },
  { name: 'Dubrovnik', country: '🇭🇷', lat: 42.6507, lon: 18.0944 },
  // Turchia
  { name: 'Istanbul', country: '🇹🇷', lat: 41.0082, lon: 28.9784 },
  { name: 'Antalya', country: '🇹🇷', lat: 36.8969, lon: 30.7133 },
  // Tunisia
  { name: 'Tunisi', country: '🇹🇳', lat: 36.8065, lon: 10.1815 },
  // Malta
  { name: 'La Valletta', country: '🇲🇹', lat: 35.8989, lon: 14.5146 },
  // Cipro
  { name: 'Limassol', country: '🇨🇾', lat: 34.7071, lon: 33.0226 },
  // Monaco
  { name: 'Monaco', country: '🇲🇨', lat: 43.7384, lon: 7.4246 },
  // Egitto
  { name: 'Alessandria', country: '🇪🇬', lat: 31.2001, lon: 29.9187 },
  // Montenegro
  { name: 'Kotor', country: '🇲🇪', lat: 42.4247, lon: 18.7712 }
];

// Calcolo fase lunare semplificato
const getMoonPhase = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  let r = year % 100;
  r %= 19;
  if (r > 9) r -= 19;
  r = ((r * 11) % 30) + month + day;
  if (month < 3) r += 2;
  r -= ((year < 2000) ? 4 : 8.3);
  r = Math.floor(r + 0.5) % 30;
  if (r < 0) r += 30;
  
  const phase = r;
  
  if (phase === 0 || phase === 29) return { name: 'Luna Nuova', emoji: '🌑', illumination: 0 };
  if (phase >= 1 && phase <= 6) return { name: 'Crescente', emoji: '🌒', illumination: Math.round((phase / 7) * 50) };
  if (phase >= 7 && phase <= 8) return { name: 'Primo Quarto', emoji: '🌓', illumination: 50 };
  if (phase >= 9 && phase <= 13) return { name: 'Gibbosa Cresc.', emoji: '🌔', illumination: 50 + Math.round(((phase - 8) / 6) * 50) };
  if (phase >= 14 && phase <= 16) return { name: 'Luna Piena', emoji: '🌕', illumination: 100 };
  if (phase >= 17 && phase <= 21) return { name: 'Gibbosa Cal.', emoji: '🌖', illumination: 100 - Math.round(((phase - 16) / 6) * 50) };
  if (phase >= 22 && phase <= 23) return { name: 'Ultimo Quarto', emoji: '🌗', illumination: 50 };
  return { name: 'Calante', emoji: '🌘', illumination: Math.round((1 - (phase - 23) / 6) * 50) };
};

// Codici meteo WMO
const getWeatherInfo = (code) => {
  const codes = {
    0: { label: 'Sereno', icon: 'sun', color: 'text-yellow-400' },
    1: { label: 'Poco nuvoloso', icon: 'sun', color: 'text-yellow-400' },
    2: { label: 'Parzialmente nuvoloso', icon: 'cloud', color: 'text-slate-300' },
    3: { label: 'Nuvoloso', icon: 'cloud', color: 'text-slate-400' },
    45: { label: 'Nebbia', icon: 'cloud', color: 'text-slate-500' },
    51: { label: 'Pioviggine', icon: 'rain', color: 'text-blue-300' },
    61: { label: 'Pioggia leggera', icon: 'rain', color: 'text-blue-400' },
    63: { label: 'Pioggia', icon: 'rain', color: 'text-blue-500' },
    65: { label: 'Pioggia forte', icon: 'rain', color: 'text-blue-600' },
    80: { label: 'Rovesci', icon: 'rain', color: 'text-blue-500' },
    95: { label: 'Temporale', icon: 'rain', color: 'text-purple-500' }
  };
  return codes[code] || { label: 'Variabile', icon: 'cloud', color: 'text-slate-400' };
};

// Stato del mare
const getSeaState = (waveHeight) => {
  if (waveHeight === null || waveHeight === undefined) return { label: 'N/D', color: 'text-slate-400', emoji: '' };
  if (waveHeight < 0.5) return { label: 'Calmo', color: 'text-emerald-400', emoji: '🟢' };
  if (waveHeight < 1.25) return { label: 'Poco mosso', color: 'text-cyan-400', emoji: '🟢' };
  if (waveHeight < 2.5) return { label: 'Mosso', color: 'text-yellow-400', emoji: '🟡' };
  if (waveHeight < 4) return { label: 'Molto mosso', color: 'text-orange-400', emoji: '🟠' };
  return { label: 'Agitato', color: 'text-red-500', emoji: '🔴' };
};

// Direzione vento
const getWindDirection = (degrees) => {
  if (degrees === null || degrees === undefined) return '';
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
  return directions[Math.round(degrees / 45) % 8];
};

// Icona meteo
const WeatherIcon = ({ type, className }) => {
  switch(type) {
    case 'sun': return <Sun className={className} />;
    case 'rain': return <CloudRain className={className} />;
    default: return <Cloud className={className} />;
  }
};

const MeteoMareSection = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCity, setSelectedCity] = useState(COASTAL_CITIES[0]);
  const [weatherData, setWeatherData] = useState(null);
  const [marineData, setMarineData] = useState(null);
  const [showCityList, setShowCityList] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  // Fetch dati
  const fetchData = async (city) => {
    setLoading(true);
    setError(null);
    
    try {
      const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&hourly=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m,relative_humidity_2m&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset&timezone=Europe%2FRome&forecast_days=5`;
      
      const marineUrl = `https://marine-api.open-meteo.com/v1/marine?latitude=${city.lat}&longitude=${city.lon}&hourly=wave_height,wave_direction,sea_surface_temperature,ocean_current_velocity&daily=wave_height_max&timezone=Europe%2FRome&forecast_days=5`;

      // Fetch meteo (sempre disponibile)
      const weatherRes = await fetch(weatherUrl);
      if (!weatherRes.ok) {
        throw new Error('Errore API meteo');
      }
      const weather = await weatherRes.json();
      setWeatherData(weather);

      // Fetch marino (potrebbe non essere disponibile per posizioni lontane dal mare)
      try {
        const marineRes = await fetch(marineUrl);
        if (marineRes.ok) {
          const marine = await marineRes.json();
          // Verifica se ci sono dati validi
          if (marine?.hourly?.wave_height && marine.hourly.wave_height.some(v => v !== null)) {
            setMarineData(marine);
          } else {
            setMarineData(null);
            toast.info('Dati marini non disponibili per questa posizione');
          }
        } else {
          setMarineData(null);
        }
      } catch (marineErr) {
        console.log('Marine data not available:', marineErr);
        setMarineData(null);
      }

    } catch (err) {
      console.error('Fetch error:', err);
      setError('Impossibile caricare i dati meteo');
      toast.error('Errore caricamento meteo');
    } finally {
      setLoading(false);
    }
  };

  // Carica dati iniziali
  useEffect(() => {
    fetchData(selectedCity);
  }, []);

  // Cambio città
  const handleCityChange = (city) => {
    setSelectedCity(city);
    setShowCityList(false);
    fetchData(city);
  };

  // Usa GPS
  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error('GPS non disponibile su questo dispositivo');
      return;
    }
    
    setLoading(true);
    toast.info('Ricerca posizione GPS...');
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const gpsCity = {
          name: 'La mia posizione',
          country: '📍',
          lat: pos.coords.latitude,
          lon: pos.coords.longitude
        };
        setSelectedCity(gpsCity);
        toast.success('Posizione trovata!');
        fetchData(gpsCity);
      },
      (err) => {
        console.error('GPS error:', err);
        setLoading(false);
        
        // Messaggi di errore specifici
        switch(err.code) {
          case err.PERMISSION_DENIED:
            toast.error('Permesso GPS negato. Attiva la localizzazione nelle impostazioni.');
            break;
          case err.POSITION_UNAVAILABLE:
            toast.error('Posizione non disponibile. Riprova all\'aperto.');
            break;
          case err.TIMEOUT:
            toast.error('Timeout GPS. Riprova.');
            break;
          default:
            toast.error('Errore GPS. Seleziona una città manualmente.');
        }
      },
      { 
        enableHighAccuracy: false, // Più veloce
        timeout: 15000, // 15 secondi
        maximumAge: 300000 // Cache 5 minuti
      }
    );
  };

  // Ottieni dati correnti
  const getCurrentData = () => {
    if (!weatherData?.hourly) return null;
    
    const now = new Date();
    const hour = now.getHours();
    const todayStr = now.toISOString().split('T')[0];
    
    let idx = 0;
    for (let i = 0; i < weatherData.hourly.time.length; i++) {
      if (weatherData.hourly.time[i].startsWith(todayStr) && 
          new Date(weatherData.hourly.time[i]).getHours() === hour) {
        idx = i;
        break;
      }
    }
    
    return {
      temp: weatherData.hourly.temperature_2m?.[idx],
      weatherCode: weatherData.hourly.weather_code?.[idx],
      windSpeed: weatherData.hourly.wind_speed_10m?.[idx],
      windDir: weatherData.hourly.wind_direction_10m?.[idx],
      humidity: weatherData.hourly.relative_humidity_2m?.[idx],
      // Dati marini (possono essere null)
      waveHeight: marineData?.hourly?.wave_height?.[idx],
      waveDir: marineData?.hourly?.wave_direction?.[idx],
      seaTemp: marineData?.hourly?.sea_surface_temperature?.[idx],
      currentSpeed: marineData?.hourly?.ocean_current_velocity?.[idx]
    };
  };

  const current = getCurrentData();
  const weatherInfo = current ? getWeatherInfo(current.weatherCode) : null;
  const seaState = current ? getSeaState(current.waveHeight) : null;

  // Formatta giorno
  const formatDay = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Oggi';
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) return 'Domani';
    return date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' });
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
          <Waves className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Barlow Condensed'}}>
          <TranslatedText text="METEO MARE" />
        </h2>
        <p className="text-cyan-400 text-sm"><TranslatedText text="Mediterraneo" /></p>
      </div>

      {/* Selettore città */}
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowCityList(!showCityList)}
            className="flex items-center gap-2 text-white"
          >
            <MapPin className="w-5 h-5 text-cyan-400" />
            <span>{selectedCity.country} {selectedCity.name}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showCityList ? 'rotate-180' : ''}`} />
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={useGPS}
              className="p-2 bg-slate-700 rounded-lg text-slate-300 hover:bg-slate-600"
              title="Usa GPS"
            >
              <Navigation className="w-4 h-4" />
            </button>
            <button
              onClick={() => fetchData(selectedCity)}
              disabled={loading}
              className="p-2 bg-slate-700 rounded-lg text-slate-300 hover:bg-slate-600"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        {showCityList && (
          <div className="mt-3 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {COASTAL_CITIES.map(city => (
              <button
                key={city.name}
                onClick={() => handleCityChange(city)}
                className={`px-2 py-1.5 rounded text-sm text-left flex items-center gap-1 ${
                  selectedCity.name === city.name
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <span>{city.country}</span>
                <span className="truncate">{city.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Contenuto */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-400">Caricamento...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12 bg-slate-900/60 border border-red-500/30 rounded-xl">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => fetchData(selectedCity)}
            className="mt-3 px-4 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Riprova
          </button>
        </div>
      ) : current ? (
        <>
          {/* Condizioni attuali */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-2xl p-5 mb-4">
            <p className="text-slate-400 text-sm mb-4">Condizioni attuali</p>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Meteo */}
              <div className="text-center">
                {weatherInfo && (
                  <WeatherIcon type={weatherInfo.icon} className={`w-14 h-14 mx-auto mb-2 ${weatherInfo.color}`} />
                )}
                <p className="text-3xl font-bold text-white">
                  {current.temp !== undefined ? `${Math.round(current.temp)}°C` : '--'}
                </p>
                <p className={`text-sm ${weatherInfo?.color || 'text-slate-400'}`}>
                  {weatherInfo?.label || 'N/D'}
                </p>
              </div>
              
              {/* Mare */}
              <div className="text-center">
                <Waves className={`w-14 h-14 mx-auto mb-2 ${seaState?.color || 'text-slate-500'}`} />
                <p className="text-3xl font-bold text-white">
                  {current.waveHeight !== undefined && current.waveHeight !== null 
                    ? `${current.waveHeight.toFixed(1)}m` 
                    : '--'}
                </p>
                <p className={`text-sm ${seaState?.color || 'text-slate-400'}`}>
                  {current.waveHeight !== undefined && current.waveHeight !== null 
                    ? `${seaState?.emoji || ''} ${seaState?.label}` 
                    : 'Dati mare N/D'}
                </p>
              </div>
            </div>

            {/* Alert mare mosso */}
            {current.waveHeight > 2 && (
              <div className="mt-4 p-3 bg-orange-500/20 border border-orange-500/40 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                <p className="text-orange-300 text-sm">Mare mosso - Attenzione per la navigazione</p>
              </div>
            )}

            {/* Avviso se posizione lontana dal mare */}
            {!marineData && (
              <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/40 rounded-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-400" />
                <p className="text-blue-300 text-sm">
                  Posizione lontana dal mare. Seleziona una città costiera per i dati marini.
                </p>
              </div>
            )}

            {/* Dettagli */}
            <div className="grid grid-cols-4 gap-2 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <Wind className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">
                  {current.windSpeed !== undefined ? `${Math.round(current.windSpeed)}` : '--'} km/h
                </p>
                <p className="text-xs text-slate-500">
                  {getWindDirection(current.windDir)}
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <Droplets className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">
                  {current.humidity !== undefined ? `${current.humidity}%` : '--'}
                </p>
                <p className="text-xs text-slate-500">Umidità</p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <Thermometer className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">
                  {current.seaTemp !== undefined ? `${current.seaTemp.toFixed(1)}°` : '--'}
                </p>
                <p className="text-xs text-slate-500">Acqua</p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <ArrowUp 
                  className="w-4 h-4 text-cyan-400 mx-auto mb-1"
                  style={{ transform: current.waveDir ? `rotate(${current.waveDir}deg)` : 'none' }}
                />
                <p className="text-white text-sm font-medium">
                  {current.currentSpeed !== undefined ? `${(current.currentSpeed * 100).toFixed(0)}` : '--'} cm/s
                </p>
                <p className="text-xs text-slate-500">Corrente</p>
              </div>
            </div>
          </div>

          {/* Widget Fase Lunare */}
          <div className="bg-gradient-to-br from-slate-900 to-indigo-950 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{getMoonPhase().emoji}</span>
                <div>
                  <p className="text-white font-bold">{getMoonPhase().name}</p>
                  <p className="text-purple-400 text-sm">{getMoonPhase().illumination}% illuminata</p>
                </div>
              </div>
              <Moon className="w-6 h-6 text-purple-400" />
            </div>
          </div>

          {/* Previsioni */}
          {weatherData?.daily?.time && (
            <div className="space-y-2">
              <h3 className="text-white font-bold mb-2">Previsioni</h3>
              
              {weatherData.daily.time.map((day, idx) => {
                const dayWeather = getWeatherInfo(weatherData.daily.weather_code?.[idx]);
                const maxWave = marineData?.daily?.wave_height_max?.[idx];
                const daySea = getSeaState(maxWave);
                
                return (
                  <div key={day} className="bg-slate-900/60 border border-slate-700 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedDay(expandedDay === idx ? null : idx)}
                      className="w-full p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <WeatherIcon type={dayWeather.icon} className={`w-6 h-6 ${dayWeather.color}`} />
                        <span className="text-white">{formatDay(day)}</span>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className="text-white">
                          {weatherData.daily.temperature_2m_max?.[idx] !== undefined 
                            ? `${Math.round(weatherData.daily.temperature_2m_max[idx])}°` 
                            : '--'}
                          {' / '}
                          {weatherData.daily.temperature_2m_min?.[idx] !== undefined 
                            ? `${Math.round(weatherData.daily.temperature_2m_min[idx])}°` 
                            : '--'}
                        </span>
                        <span className={daySea.color}>
                          {daySea.emoji} {maxWave !== undefined ? `${maxWave.toFixed(1)}m` : '--'}
                        </span>
                        {expandedDay === idx ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    
                    {expandedDay === idx && (
                      <div className="px-3 pb-3 grid grid-cols-2 gap-2 border-t border-slate-700/50 pt-2">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Sunrise className="w-4 h-4 text-orange-400" />
                          Alba: {weatherData.daily.sunrise?.[idx]?.split('T')[1]?.slice(0,5) || '--'}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Sunset className="w-4 h-4 text-orange-500" />
                          Tramonto: {weatherData.daily.sunset?.[idx]?.split('T')[1]?.slice(0,5) || '--'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Credits */}
          <p className="text-center text-slate-600 text-xs mt-4">
            Dati: Open-Meteo.com
          </p>
        </>
      ) : (
        <div className="text-center py-12 text-slate-400">
          Nessun dato disponibile
        </div>
      )}
    </div>
  );
};

export default MeteoMareSection;
